import React, {useEffect, useMemo, useRef, useState} from 'react';
import '../styles/Filter.css';
import {Col, Row} from 'react-bootstrap';
import {
    useCMWorklistContext
} from '../../../../views/caremgmt/worklist/components/CMWorklistModals/context/CMWorklist.context';

function Filter({
  header,
  index,
  onToggle = () => { },
  children,
  componentName,
  idFragment,
  keepOpened = false
}) {
  const context = useCMWorklistContext();
  const activeRoleLeadId = context?.activeRole?.leadId;
  const [collapsed, setCollapsed] = useState(!keepOpened);
  const collapseRef = useRef(null);
  const collapseInstanceRef = useRef(null);
  const isInitializedRef = useRef(false);
  const previousLeadIdRef = useRef(activeRoleLeadId);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const initializeCollapse = () => {
    if (!collapseRef.current || typeof window.bootstrap === 'undefined') {
      return null;
    }

    const element = collapseRef.current;
    if (!element.classList.contains('collapse')) {
      element.classList.add('collapse');
    }

    try {
      const instance = new window.bootstrap.Collapse(element, {
        toggle: false
      });
      return instance;
    } catch (error) {
      console.warn('Failed to initialize collapse:', error);
      return null;
    }
  };

  useEffect(() => {
    const currentLeadId = activeRoleLeadId;
    if (!isFirstRender && previousLeadIdRef.current !== currentLeadId) {
      if (!keepOpened) {
        setCollapsed(true);
        onToggle(true);
        requestAnimationFrame(() => {
          if (collapseInstanceRef.current && isInitializedRef.current) {
            try {
              collapseInstanceRef.current.hide();
            } catch (error) {
              console.warn('Failed to hide collapse on leadId change:', error);
            }
          }
        });
      }
    }
    previousLeadIdRef.current = currentLeadId;
  }, [activeRoleLeadId, keepOpened, onToggle, isFirstRender]);

  useEffect(() => {
    if (keepOpened) {
      setCollapsed(false);
    }
  }, [keepOpened]);

  useEffect(() => {
    let mounted = true;

    const setup = () => {
      if (!mounted || !collapseRef.current) return;
      if (collapseInstanceRef.current) {
        try {
          collapseInstanceRef.current.dispose();
          collapseInstanceRef.current = null;
        } catch (error) {
          console.warn('Failed to dispose previous collapse:', error);
        }
      }
      requestAnimationFrame(() => {
        if (!mounted) return;

        const instance = initializeCollapse();
        if (instance) {
          collapseInstanceRef.current = instance;
          isInitializedRef.current = true;
          if (keepOpened) {
            instance.show();
          } else if (collapsed && !isFirstRender) {
            instance.hide();
          }
        }
        if (isFirstRender) {
          setIsFirstRender(false);
        }
      });
    };

    setup();

    return () => {
      mounted = false;
      if (collapseInstanceRef.current) {
        try {
          collapseInstanceRef.current.dispose();
          collapseInstanceRef.current = null;
          isInitializedRef.current = false;
        } catch (error) {
          console.warn('Failed to dispose collapse:', error);
        }
      }
    };
  }, [keepOpened, collapsed, isFirstRender]);

  const handleToggle = () => {
    if (keepOpened) return;

    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onToggle(newCollapsed);

    requestAnimationFrame(() => {
      if (collapseInstanceRef.current && isInitializedRef.current) {
        try {
          if (newCollapsed) {
            collapseInstanceRef.current.hide();
          } else {
            collapseInstanceRef.current.show();
          }
        } catch (error) {
          console.warn('Failed to toggle collapse:', error);
          const instance = initializeCollapse();
          if (instance) {
            collapseInstanceRef.current = instance;
            requestAnimationFrame(() => {
              instance[newCollapsed ? 'hide' : 'show']();
            });
          }
        }
      }
    });
  };

  const memoizedChildren = useMemo(() => children, [children]);

  return (
    <Row xl={1}>
      <Col id={`${componentName}-${idFragment}-filter`} className="border-bottom p-0">
        <div className='accordion-item'>
          <h2
            className='accordion-header'
            id={`heading-${index}`}
            onClick={handleToggle}
          >
            <button
              className={`accordion-button ${!keepOpened && collapsed ? 'collapsed' : ''}`}
              type='button'
              aria-expanded={keepOpened || !collapsed}
              aria-controls={`collapse-${index}`}
              id={`accordion-${index}`}
            >
              <i className={`fa fa-chevron-${keepOpened || !collapsed ? 'down' : 'right'}`} aria-hidden="true"></i>
              {header}
            </button>
          </h2>
          <div
            id={`collapse-${index}`}
            className={`accordion-collapse collapse ${keepOpened || !collapsed ? 'show' : ''}`}
            aria-labelledby={`heading-${index}`}
            ref={collapseRef}
          >
            <div className='accordion-body'>
              {memoizedChildren}
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
}

export default Filter;
